import { AfterViewInit, Component, Inject, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { ErrorScreenLayoutComponent } from '../shared/error-screen-layout.component';
import {
  ErrorScreenCopy,
  ErrorScreenCopyByLocale,
} from '../shared/error-screen-copy';
import { resolveErrorScreenLocale } from '../shared/error-screen-locale';
import { ErrorScreenTelemetryService } from 'src/app/core/services/error-screen-telemetry.service';
import { ErrorScreenRecoveryService } from 'src/app/core/services/error-screen-recovery.service';
import {
  ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY,
  ERROR_SCREEN_CAUSE_STATE_KEY,
  ErrorScreenRecoveryAction,
  normalizeArrivalReason,
} from 'src/app/core/domain/error-screen';
import { createUnicornIncidentCode } from 'src/app/core/domain/unicorn';

/**
 * Spec 051 (error-screen-parity).
 * Phase 3: FR-008 translated (en/es + English fallback), FR-009 no status-code
 * heading, FR-010 independent of the failing application.
 * Phase 2: this is the auth-redirect destination — the Auth0 callback routes an
 * expired/invalid session here with `cause: auth`. FR-007: where the recorded
 * condition is an auth session, the offered action is to authenticate again.
 * A plain content `422` (an http-422 validation) instead offers a return to a
 * working state (`/` is not a 422 source). FR-012 records whether it worked.
 */
export const PAGE_ERROR_422_COPY = {
  en: {
    logoAlt: 'rettX',
    heading: 'We couldn’t complete that request',
    body:
      'Something in what was submitted couldn’t be processed. Please check the details you entered and try again, or head back to the home screen.',
    signInLink: 'Sign in again',
    homeLink: 'Go to the home screen',
  },
  es: {
    logoAlt: 'rettX',
    heading: 'No pudimos completar esa solicitud',
    body:
      'No se pudo procesar algo de lo que se envió. Revisa los datos que introdujiste e inténtalo de nuevo, o vuelve a la pantalla de inicio.',
    signInLink: 'Iniciar sesión de nuevo',
    homeLink: 'Ir a la pantalla de inicio',
  },
} satisfies ErrorScreenCopyByLocale;

@Component({
  selector: 'app-error-422',
  standalone: true,
  imports: [ErrorScreenLayoutComponent],
  templateUrl: './page-error-422.component.html',
  styleUrl: './page-error-422.component.scss',
})
export class PageError422Component implements AfterViewInit {
  private telemetry = inject(ErrorScreenTelemetryService);
  private recovery = inject(ErrorScreenRecoveryService);
  private recorded = false;
  readonly copy: ErrorScreenCopy;
  readonly incidentCode = createUnicornIncidentCode();
  private readonly arrivalReason: string;
  private readonly cause: unknown;
  /** FR-007: an expired/invalid session recovers by re-authenticating. */
  readonly isAuthCause: boolean;

  constructor(@Inject(DOCUMENT) document: Document) {
    this.copy = PAGE_ERROR_422_COPY[resolveErrorScreenLocale(document)];
    const handoff = this.telemetry.readHandoffState();
    this.arrivalReason = normalizeArrivalReason(
      handoff?.[ERROR_SCREEN_ARRIVAL_REASON_STATE_KEY],
    );
    this.cause = handoff?.[ERROR_SCREEN_CAUSE_STATE_KEY];
    this.isAuthCause = this.cause === 'auth';
  }

  // Spec 051 FR-001/FR-002/FR-004: record this screen's appearance after render.
  // This is the auth-redirect destination (CallbackComponent routes Auth0 errors
  // here), so it carries the machine-readable reason the provider handed over —
  // captured for diagnosis, never displayed, never the free-text description.
  ngAfterViewInit(): void {
    if (this.recorded) {
      return;
    }
    this.recorded = true;
    this.telemetry.recordScreenShown('unprocessable', {
      cause: this.cause,
      arrivalReason: this.arrivalReason,
    });
  }

  // Spec 051 Phase 2 FR-005/FR-007: re-authenticate for an auth session,
  // otherwise return to the caregiver's start. Never a dead end.
  recover(action: ErrorScreenRecoveryAction): void {
    this.recovery.recover({
      screen: 'unprocessable',
      action,
      incidentCode: this.incidentCode,
      arrivalReason: this.arrivalReason,
      destination: action === 'interactive-reauth' ? '/patients' : '/',
    });
  }
}
